<template>
  <b-card no-body>
    <b-card-header>
      <div class="d-flex align-items-center card-title-responsive-40">
        <feather-icon
          size="24"
          class="mr-1 d-none d-sm-block"
          icon="CheckSquareIcon"
        />
        <b-card-title class="ml-25">
          {{ $t('projects.list-tasks') }}
        </b-card-title>
      </div>

      <b-button-group class="ml-1">
        <b-button
          v-if="isUserAllowWrite && item === null && !isUserLimitedProjects"
          class="btn-icon"
          variant="outline-primary"
          @click="addNew()"
        >
          <feather-icon
            class="text-nowrap d-block d-sm-none"
            icon="FileIcon"
          />
          <span class="text-nowrap d-none d-sm-block">{{ $t('projects.add-new-task') }}</span>
        </b-button>

        <b-button
          v-if="isUserAllowWrite && item === null && !isUserLimitedProjects && wizardTasks && wizardTasks.length > 0"
          id="btn-wizard"
          variant="outline-primary"
          class="btn-icon"
          @click="wizard()"
        >
          <feather-icon icon="ZapIcon" />
          <b-tooltip
            triggers="hover"
            target="btn-wizard"
            :title=" $t('projects.tasks-wizard')"
            :delay="{ show: 100, hide: 50 }"
          />
        </b-button>

        <b-button
          v-if="item !== null"
          class="btn-icon"
          variant="outline-primary"
          @click="close()"
        >
          <feather-icon
            class="text-nowrap d-block d-sm-none"
            icon="XIcon"
          />
          <span class="text-nowrap d-none d-sm-block">{{ $t('common.close') }}</span>
        </b-button>

        <b-button
          v-if="item !== null"
          class="btn-icon"
          variant="outline-primary"
          :disabled="!somethingChanged || !isValidate"
          @click="submit()"
        >
          <feather-icon
            class="text-nowrap d-block d-sm-none"
            icon="SaveIcon"
          />
          <span class="text-nowrap d-none d-sm-block">{{ $t('common.save') }}</span>
        </b-button>

      </b-button-group>
    </b-card-header>

    <b-card-body>
      <app-timeline v-if="item === null">
        <app-timeline-item
          v-for="(a, index) in tasks"
          :key="index"
          :icon="resolveTaskIcon(a.status)"
          :variant="resolveTaskVariant(a.status)"
          :class="index % 2 === 0 ? 'odd' : ''"
        >
          <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-0">
            <h6><a @click="edit(a)">{{ a.title }}</a></h6>
            <div>
              <small class="text-muted pr-1">{{ formatDateHumanFriendly(a.lastUpdate) }}</small>
              <b-dropdown
                v-if="isUserAllowWrite"
                variant="link"
                no-caret
                toggle-class="p-0 mr-1"
                right
              >
                <template #button-content>
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="16"
                    class="align-middle text-body"
                  />
                </template>
                <b-dropdown-item
                  v-for="(s, k) in taskStateOptions"
                  :key="k"
                  @click="submitTaskState(a, s)"
                >
                  <feather-icon
                    v-if="s.value === a.status"
                    icon="CheckIcon"
                    size="16"
                    class="align-middle"
                  /> {{ s.text }}
                </b-dropdown-item>
                <b-dropdown-divider />
                <b-dropdown-item
                  v-if="!isUserLimitedProjects"
                  @click="deleete(a)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="16"
                    class="align-middle"
                  />
                  {{ $t('common.delete') }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
          <p v-if="a.notes">
            <small>{{ a.notes }}</small>
          </p>
        </app-timeline-item>
      </app-timeline>

      <div
        v-if="item === null && tasks.length === 0"
        class="text-center odd pt-1 pb-1"
      >
        <div>
          {{ $t('common.no-matching') }}
        </div>
      </div>
      <b-form v-if="item !== null">
        <b-row>
          <b-col
            cols="12"
            lg="12"
          >
            <b-form-group
              :label="$t('projects.tasks-title')"
              label-for="tasks-title"
            >
              <b-form-input
                id="tasks-title"
                v-model="item.title"
                trim
              />
            </b-form-group>

            <b-form-group
              :label="$t('projects.tasks-status')"
              label-for="tasks-status"
              label-class="mb-1"
            >
              <b-form-radio-group
                id="tasks-status"
                v-model="item.status"
                :options="taskStateOptions"
              />
            </b-form-group>

            <b-form-group
              :label="$t('common.notes')"
              label-for="notes"
            >
              <div class="form-label-group ">
                <b-form-textarea
                  id="notes"
                  v-model="item.notes"
                  rows="4"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col
            v-if="item.lastUpdate"
            cols="12"
            md="12"
          >
            <feather-icon
              icon="Edit3Icon"
              class="mr-75"
            />
            <span class="font-weight-bold mr-75">{{ $t('common.last-update') }}:</span>
            {{ formatDateHumanFriendly(item.lastUpdate) }}
          </b-col>
        </b-row>
      </b-form>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardBody, BCardHeader, BCardTitle, BButton, BButtonGroup, BForm, BRow, BCol, BDropdown, BDropdownItem, BFormInput, BFormGroup, BFormRadioGroup, BFormTextarea, BDropdownDivider, BTooltip,
} from 'bootstrap-vue'
import lookupsService from '@/vue.lookups.service'

import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import authService from '@/vue.auth.service'
import {
  resolveTaskVariant, resolveTaskIcon, formatDateHumanFriendly, clone, swalDeleteOptions, removeFromArray,
} from '../layouts/layouts.utils'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BButton,
    BDropdownItem,
    BDropdownDivider,
    BButtonGroup,
    AppTimeline,
    AppTimelineItem,
    BForm,
    BFormInput,
    BFormGroup,
    BFormTextarea,
    BDropdown,
    BFormRadioGroup,
    BTooltip,
  },
  props: {
    tasks: {
      type: Array,
      default: null,
    },
    wizardTasks: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      isUserAllowWrite: authService.isUserInRole('write'),
      isUserLimitedProjects: authService.isUserInRole('limit-projects'),
      item: null,
      original: null,
      somethingChanged: false,
      isValidate: false,

    }
  },
  async mounted() {
    this.$watch('item', () => {
      if (this.item) {
        this.somethingChanged = true
        this.isValidate = (this.item.title)
      }
    }, { deep: true })
  },
  methods: {
    reset() {
      this.item = null
      this.original = null
    },
    addNew() {
      this.item = {
        title: '',
        notes: null,
        lastUpdate: new Date(),
        status: 'todo',
      }
      this.original = null
      this.somethingChanged = false
    },
    async edit(item) {
      if (this.isUserAllowWrite) {
        this.item = item
        this.original = clone(item)
        this.somethingChanged = false
      }
    },
    close() {
      if (this.original) {
        this.item.title = this.original.title
        this.item.notes = this.original.notes
        this.item.status = this.original.status
        this.item.lastUpdate = this.original.lastUpdate
      }
      this.original = null
      this.item = null
    },
    submit() {
      this.item.lastUpdate = new Date()
      if (this.original === null) { // new item
        this.tasks.push(this.item)
      }
      this.$emit('on-submit')
      this.reset()
    },
    submitTaskState(task, status) {
      // eslint-disable-next-line no-param-reassign
      task.status = status.value
      // eslint-disable-next-line no-param-reassign
      task.lastUpdate = new Date()
      this.$emit('on-submit')
    },
    deleete(task) {
      this.$swal(swalDeleteOptions).then(result => {
        if (result.value === true) {
          removeFromArray(this.tasks, task)
          this.$emit('on-submit')
          this.reset()
        }
      })
    },
    wizard() {
      const missing = this.wizardTasks.filter(w => !this.tasks.find(a => a.title === w)).map(w => ({ title: w, status: 'todo' }))
      if (missing && missing.length > 0) {
        this.tasks.push(...missing)
        this.$emit('on-submit')
      }
    },
  },
  setup() {
    const taskStateOptions = lookupsService.getTaskStateOptions()
    return {
      resolveTaskVariant,
      resolveTaskIcon,
      formatDateHumanFriendly,
      taskStateOptions,
    }
  },
}
</script>

<style lang="scss">
 .timeline-item   {
   padding-top: 0.25rem !important;
   padding-bottom: 0.5rem !important;
 }

</style>
